import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getContractDetail,
  deleteContract,
  updateContract,
} from "../../api/contract/info";
import TableCell from "../../components/common/ui/TableCell";
import SidePanel from "../../components/common/layout/SidePanel";
import AttachmentBox from "../../components/common/papers/AttachmentBox";
import FileList from "../../components/common/papers/FileList";
import CompanySearch from "../../components/contract/CompanySearch";
import { softDeleteDoc } from "../../api/papers";
import { formatDate } from "../../utils/date";

const emptyMeta = {
  revision: 1,
  version: 1,
  is_current: true,
  contract_code: "",
  confirmer_company_name: "",
  construction_type: "본공사",
  amount: "",
  signed_date_actual: "",
  start_date_actual: "",
  end_date_actual: "",
  sign_date_expected: "",
  end_date_expected: "",
  contract_type: "",
  memo: "",
};

const normalize = (data) => ({
  dns: data?.dns ?? "",
  title: data?.title ?? "",
  created_at: data?.created_at ?? null,
  created_by: data?.created_by ?? "",
  versions: data?.versions ?? [],
  meta: { ...emptyMeta, ...(data?.meta ?? {}) },
});

const Info_view = ({
  contractId: propContractId,
  onDeleted,
  onUpdated,
  onClose,
}) => {
  const { contractId: routeContractId } = useParams();
  const navigate = useNavigate();
  const contractId = propContractId || routeContractId;

  const [modalOpen, setModalOpen] = useState(false);
  const [contract, setContract] = useState(normalize({}));
  const [form, setForm] = useState(normalize({}));
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [editing, setEditing] = useState(false);

  const [loadedFiles, setLoadedFiles] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  const attachmentRef = useRef(null);

  const fetchDetail = useCallback(async (id = contractId) => {
    if (!id) return;
    setLoading(true);
    setErrMsg("");
    try {
      const data = await getContractDetail(id);
      const norm = normalize(data);
      setContract(norm);
      setForm(norm);
    } catch (e) {
      setErrMsg("계약 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleTopChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMetaChange = (e) => {
    setForm({
      ...form,
      meta: { ...form.meta, [e.target.name]: e.target.value },
    });
  };

  const handleSave = async () => {
    try {
      const fd = new FormData();
      fd.append("title", form.title ?? "");
      fd.append("meta", JSON.stringify(form.meta));

      const res = await updateContract(contractId, fd);

      await attachmentRef.current?.save(res.dns);

      alert("수정되었습니다");
      setEditing(false);

      await fetchDetail(res?.dns ?? contractId);
      onUpdated?.(res?.dns ?? contractId);
    } catch (err) {
      alert("수정 실패. 다시 시도해주세요.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("계약과 모든 버전 문서가 삭제됩니다.\n정말 삭제하시겠습니까?")) return;

    try {
      setDeleting(true);

      const dnsList = [contract.dns];
      if (contract.versions?.length) {
        dnsList.push(...contract.versions.map((v) => v.dns));
      }

      await Promise.all(dnsList.map((dns) => softDeleteDoc(dns, true)));

      alert("삭제되었습니다.");
      onDeleted?.();
    } catch {
      alert("삭제 중 오류 발생");
    } finally {
      setDeleting(false);
    }
  };

  const headerMeta = contract.meta;
  const versions = useMemo(() => contract.versions ?? [], [contract.versions]);

  if (loading) return <div className="p-6 text-center">로딩중...</div>;
  if (errMsg) return <div className="p-6 text-center text-red-500">{errMsg}</div>;

  return (
    <div className=" p-6 bg-white rounded-xl shadow space-y-8">

      {/* ===== 페이지 제목 ===== */}
      {/* <div>
        <h2 className="text-title">계약 상세 정보</h2>
        <div className="h-px bg-gray-300 mt-2"></div>
      </div> */}

      {/* ===== 상단 버튼 영역 ===== */}
      <div className="flex flex-wrap justify-between items-center">

        {/* ---- 버전 버튼 ---- */}
        <button
          onClick={() => setShowVersionPanel(true)}
          className="px-4 py-2 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
        >
          Rev.{headerMeta.revision} v{headerMeta.version}{" "}
          {headerMeta.is_current ? "(최신)" : "(과거)"}
        </button>

        {/* ---- 수정/삭제/닫기 ---- */}
        <div className="flex gap-2 flex-wrap">
          {editing ? (
            <>
              <button onClick={handleSave} className="btn-save">저장</button>
              <button
                onClick={() => {
                  setEditing(false);
                  setForm(contract);
                }}
                className="btn-basic"
              >
                취소
              </button>
            </>
          ) : (
            headerMeta.is_current && (
              <button onClick={() => setEditing(true)} className="btn-edit">
                수정
              </button>
            )
          )}

          {headerMeta.is_current && (
            <button onClick={handleDelete} className="btn-delete">
              삭제
            </button>
          )}

          {onClose && (
            <button onClick={onClose} className="btn-basic">
              닫기
            </button>
          )}
        </div>
      </div>

      {/* ===== 상세 정보 테이블 ===== */}
      <div className="overflow-hidden">
        <table className="min-w-full text-sm ">
          <colgroup>
              <col />
              <col style={{ width: "380px" }} />
            </colgroup>
          <tbody>

            {/* 계약명 / 업데이트 시간 */}
            <tr>
              <TableCell
                label="계약명"
                editing={editing}
                value={contract.title}
                input={
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleTopChange}
                    className="input-base"
                  />
                }
              />

              <TableCell
                label="수정일"
                value={
                  contract.created_at
                    ? new Date(contract.created_at).toISOString().slice(0, 10).replace(/-/g, ". ")
                    : "-"
                }
              />
            </tr>

            {/* 계약 구분 / 코드 */}
            <tr>
              <TableCell
                label="계약 구분"
                editing={editing}
                value={contract.meta.contract_type}
                input={
                  <select
                    name="contract_type"
                    value={form.meta.contract_type}
                    onChange={handleMetaChange}
                    className="input-base"
                  >
                    <option value="">선택</option>
                    <option value="지명경쟁입찰">지명경쟁입찰</option>
                    <option value="제한경쟁입찰">제한경쟁입찰</option>
                    <option value="일반경쟁입찰">일반경쟁입찰</option>
                    <option value="수의계약">수의계약</option>
                    <option value="협상계약">협상계약</option>
                    <option value="최저가낙찰">최저가낙찰</option>
                    <option value="종합평가낙찰">종합평가낙찰</option>
                  </select>
                }
              />

              <TableCell
                label="계약 코드"
                editing={editing}
                value={contract.meta.contract_code}
                input={
                  <input
                    type="text"
                    name="contract_code"
                    value={form.meta.contract_code}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />
            </tr>

            {/* Revision / 공사 구분 */}
            <tr>
              <TableCell
                label="Revision"
                editing={editing}
                value={`Rev.${contract.meta.revision}`}
                input={
                  <input
                    type="number"
                    name="revision"
                    value={form.meta.revision}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />

              <TableCell
                label="공사 구분"
                editing={editing}
                value={contract.meta.construction_type}
                input={<div>
                  <input
                    type="text"
                    name="construction_type"
                    value={form.meta.construction_type}
                    onChange={handleMetaChange}
                    className="w-full border rounded-md px-3 py-2"
                    placeholder="공종을 입력하세요"
                  />
                </div>}
                // input={
                //   <select
                //     name="construction_type"
                //     value={form.meta.construction_type}
                //     onChange={handleMetaChange}
                //     className="input-base"
                //   >
                //     <option value="본공사">본공사</option>
                //     <option value="전기공사">전기공사</option>
                //     <option value="통신공사">통신공사</option>
                //     <option value="소방공사">소방공사</option>
                //   </select>
                // }
              />
            </tr>

            {/* 작성자 / 계약업체 */}
            <tr>
              <TableCell label="작성자" value={contract.created_by} />

              <TableCell
                label="주계약업체"
                editing = {editing}
                value = {contract.meta.confirmer_company_name}
                input={
                  <div className="flex items-center gap-2 w-full">

                    {/* 직접입력 체크 */}
                    <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={form.meta.manualCompanyInput}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            meta: {
                              ...form.meta,
                              manualCompanyInput: e.target.checked,
                              confirmer_company_name: "",
                            },
                          })
                        }
                      />
                      직접 입력
                    </label>

                    {form.meta.manualCompanyInput ? (
                      /* 직접 입력 */
                      <input
                        type="text"
                        name="confirmer_company_name"
                        value={form.meta.confirmer_company_name}
                        onChange={handleMetaChange}
                        className="input-base flex-1"
                        placeholder="업체명 입력"
                      />
                    ) : (
                      /* 검색 선택 */
                      <div className="flex flex-1 gap-2">
                        <input
                          type="text"
                          value={form.meta.confirmer_company_name}
                          readOnly
                          className="input-base flex-1 bg-gray-100"
                        />
                        <button
                          type="button"
                          onClick={() => setModalOpen(true)}
                          className="btn-basic px-3 py-1 whitespace-nowrap"
                        >
                          검색
                        </button>
                      </div>
                    )}

                    {/* 업체 검색 모달 */}
                    <CompanySearch
                      open={modalOpen}
                      onClose={() => setModalOpen(false)}
                      onSelect={(company) =>
                        setForm({
                          ...form,
                          meta: {
                            ...form.meta,
                            confirmer_company_name: company.name,
                            manualCompanyInput: false,
                          },
                        })
                      }
                    />
                  </div>
                }
              />
            </tr>

            {/* 금액 / 최초 계약일 */}
            <tr>
              <TableCell
                label="계약 금액"
                editing={editing}
                value={
                  contract.meta.amount
                    ? `${Number(contract.meta.amount).toLocaleString()} 원`
                    : "-"
                }
                input={
                  <input
                    type="number"
                    name="amount"
                    value={form.meta.amount}
                    onChange={handleMetaChange}
                    className="input-base text-right"
                  />
                }
              />

              <TableCell
                label="최초 계약일"
                editing={editing}
                value={formatDate(contract.meta.signed_date_actual)}
                input={
                  <input
                    type="date"
                    name="signed_date_actual"
                    value={form.meta.signed_date_actual || ""}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />
            </tr>

            {/* 착수 / 종료 */}
            <tr>
              <TableCell
                label="계약 착수일"
                editing={editing}
                value={formatDate(contract.meta.start_date_actual)}
                input={
                  <input
                    type="date"
                    name="start_date_actual"
                    value={form.meta.start_date_actual || ""}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />

              <TableCell
                label="계약 종료일"
                editing={editing}
                value={formatDate(contract.meta.end_date_actual)}
                input={
                  <input
                    type="date"
                    name="end_date_actual"
                    value={form.meta.end_date_actual || ""}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />
            </tr>

            {/* 예정일 */}
            <tr>
              <TableCell
                label="계약 예정일"
                editing={editing}
                value={formatDate(contract.meta.sign_date_expected)}
                input={
                  <input
                    type="date"
                    name="sign_date_expected"
                    value={form.meta.sign_date_expected || ""}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />

              <TableCell
                label="종료 예정일"
                editing={editing}
                value={formatDate(contract.meta.end_date_expected)}
                input={
                  <input
                    type="date"
                    name="end_date_expected"
                    value={form.meta.end_date_expected || ""}
                    onChange={handleMetaChange}
                    className="input-base"
                  />
                }
              />
            </tr>

            {/* 특이사항 */}
            <tr>
              <TableCell
                label="특이사항"
                editing={editing}
                colSpan={3}
                value={
                  <div className="whitespace-pre-line leading-relaxed p-1">
                    {contract.meta.memo || "-"}
                  </div>
                }
                input={
                  <textarea
                    name="memo"
                    value={form.meta.memo}
                    onChange={handleMetaChange}
                    className="input-base h-36 resize-none"
                  />
                }
              />
            </tr>

          </tbody>
        </table>
      </div>

      {/* ===== 첨부파일 ===== */}
      <div className="bg-white border rounded-xl p-4">
        {editing ? (
          <AttachmentBox ref={attachmentRef} initial={loadedFiles} />
        ) : (
          <FileList
            dns={contract.dns}
            onFilesLoaded={(files) => {
              setLoadedFiles(
                files.map((f) => ({
                  _type: "library",
                  id: f.file_id ?? f.id,
                  name: f.name,
                }))
              );
            }}
          />
        )}
      </div>

      {/* ===== 버전 패널 ===== */}
      <SidePanel
        isOpen={showVersionPanel}
        onClose={() => setShowVersionPanel(false)}
        title="수정 내역"
        width={700}
      >
        <div className="p-4">
          <table className="min-w-full text-sm border">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 border">Rev</th>
                <th className="px-4 py-2 border">Ver</th>
                <th className="px-4 py-2 border">계약명</th>
                <th className="px-4 py-2 border">수정 날짜</th>
              </tr>
            </thead>

            <tbody>
              {versions.length ? (
                versions.map((v) => (
                  <tr
                    key={v.dns}
                    className="border hover:bg-blue-50 cursor-pointer"
                    onClick={() => {
                      fetchDetail(v.dns);
                      setShowVersionPanel(false);
                      setEditing(false);
                    }}
                  >
                    <td className="border px-2 py-1 text-center">{v.revision}</td>
                    <td className="border px-2 py-1 text-center">{v.version}</td>
                    <td className="border px-2 py-1">{v.title}</td>
                    <td className="border px-2 py-1 text-gray-600">
                      {v.created_at
                        ? new Date(v.created_at).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-500">
                    수정 내역 없음
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SidePanel>

    </div>
  );
};

export default Info_view;
